import { useState, useEffect, useCallback } from "react";
import { useRecordContext, useNotify } from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { QRCodeCanvas as _QRCodeCanvas } from "qrcode.react";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const QRCodeCanvas = _QRCodeCanvas as any;

import { API_URL, getAuthHeaders } from "../../dataProvider";

const VIEWER_URL = import.meta.env.VITE_VIEWER_URL ?? "";

interface Perspective {
  id: string;
  area_id: string;
  name: string;
  camera_x: number;
  camera_y: number;
  camera_z: number;
  target_x: number;
  target_y: number;
  target_z: number;
  created_at: string;
}

interface PerspectivesPanelProps {
  /**
   * Bump this (e.g. from a counter) to force a refetch — react-admin's
   * refresh() re-fetches the model record, but since this panel fetches
   * independently of the data provider, a same-id refresh doesn't change
   * fetchPerspectives's memoized reference and the effect below won't refire
   * on its own. Used after a model upload auto-creates "Vista predefinita".
   */
  refreshKey?: number;
}

export function PerspectivesPanel({ refreshKey }: PerspectivesPanelProps) {
  const record = useRecordContext();
  const notify = useNotify();
  const [perspectives, setPerspectives] = useState<Perspective[]>([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchPerspectives = useCallback(async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/perspectives?area_id=${record.id as string}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Perspective[];
      setPerspectives(data);
    } catch {
      notify("Errore nel caricamento delle prospettive", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [record?.id, notify]);

  useEffect(() => {
    void fetchPerspectives();
  }, [fetchPerspectives, refreshKey]);

  const handleDelete = useCallback(
    async (id: string) => {
      setRemovingId(id);
      try {
        const res = await fetch(`${API_URL}/admin/perspectives/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setPerspectives((prev) => prev.filter((p) => p.id !== id));
        notify("Prospettiva eliminata", { type: "success" });
      } catch {
        notify("Errore durante l'eliminazione", { type: "error" });
      } finally {
        setRemovingId(null);
      }
    },
    [notify]
  );

  const handleDownloadQr = useCallback((id: string, name: string) => {
    const canvas = document.getElementById(`qr-canvas-${id}`) as HTMLCanvasElement | null;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${name}.png`;
    a.click();
  }, []);

  const handleCopyLink = useCallback(
    async (url: string) => {
      await navigator.clipboard.writeText(url);
      notify("Link copiato negli appunti", { type: "success" });
    },
    [notify]
  );

  if (!record) return null;

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Prospettive salvate
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Caricamento...</Typography>
        </Box>
      ) : perspectives.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessuna prospettiva salvata.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Cam X</TableCell>
              <TableCell align="right">Cam Y</TableCell>
              <TableCell align="right">Cam Z</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {perspectives.map((p) => {
              const url = `${VIEWER_URL}/?model=${p.area_id}&view=${p.id}`;
              const token = localStorage.getItem("aere_access_token");
              const adminUrl = token ? `${url}&token=${token}` : url;
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <a
                      href={adminUrl}
                      target="_self"
                      rel="noopener noreferrer"
                      style={{ color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                    >
                      {p.name}
                    </a>
                  </TableCell>
                  <TableCell align="right">{p.camera_x.toFixed(3)}</TableCell>
                  <TableCell align="right">{p.camera_y.toFixed(3)}</TableCell>
                  <TableCell align="right">{p.camera_z.toFixed(3)}</TableCell>
                  <TableCell>
                    {VIEWER_URL && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <QRCodeCanvas
                          id={`qr-canvas-${p.id}`}
                          value={url}
                          size={512}
                          style={{ display: "none" }}
                        />
                        <Tooltip title="Scarica QR">
                          <IconButton
                            aria-label="Scarica QR"
                            onClick={() => handleDownloadQr(p.id, p.name)}
                            size="small"
                          >
                            <QrCode2Icon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copia link">
                          <IconButton
                            aria-label="Copia link"
                            onClick={() => void handleCopyLink(url)}
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="Elimina prospettiva"
                      onClick={() => void handleDelete(p.id)}
                      disabled={removingId === p.id}
                      size="small"
                    >
                      {removingId === p.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
